import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type EmploymentFormSchema, employmentFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { EmploymentForm } from "@/forms/employment-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditEmploymentModalProps extends ModalProps {
	userId: string;
	employmentId: string;
	onComplete: () => Promise<void>;
}

export function EditEmploymentModal({
	isOpen,
	onClose,
	onComplete,
	userId,
	employmentId,
}: EditEmploymentModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.users[":userId"].employments[":employmentId"].$get({
			param: { userId, employmentId },
		});
		const json = await responseHandler(response);
		return employmentFormSchema.parse(json.employment);
	});
	const methods = useForm<EmploymentFormSchema>({
		resolver: resolver(employmentFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: EmploymentFormSchema) {
		const response = await client.users[":userId"].employments[":employmentId"].$patch({
			param: { userId, employmentId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate([userId, "employments"]) });
		await onComplete();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("personnel.employment.edit_employment")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<EmploymentForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
